import { storyblokEditable } from "@storyblok/react/rsc";
import Link from "next/link";

import { ListItem } from "@/components/layout/ListItem";
import { ArrowRightThin } from "@/components/icons";
import type { ListItemIconName } from "@/components/icons";

import type {
  AssetStoryblok,
  MultilinkStoryblok,
} from "@/component-types-sb";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

type IconColor = "default" | "sky" | "sand" | "sun";

const iconBgClasses: Record<IconColor, string> = {
  default: "bg-gray-25",
  sky: "bg-sky-50",
  sand: "bg-sand-50",
  sun: "bg-sun-50",
};

type InfoIconListItemStoryblok = {
  icon: ListItemIconName;
  custom_icon?: AssetStoryblok;
  text?: string;
  title?: string;
  show_icon_background?: boolean;
  icon_color?: IconColor;
  link?: MultilinkStoryblok;
  _uid: string;
  component: "info_icon_list_item";
  [k: string]: any;
};

type InfoIconListItemProps = {
  blok: InfoIconListItemStoryblok;
};

const InfoIconListItem = ({ blok }: InfoIconListItemProps) => {
  const color: IconColor = (blok.icon_color as IconColor) || "sky";
  const href = getUrlFromLinkObject(blok.link) || undefined;

  // With circle background: icon in circle + title (bold) + text (regular)
  if (blok.show_icon_background) {
    const content = (
      <div className="flex items-center">
        <ListItem
          variant="tall"
          icon={blok.icon}
          customIconUrl={blok.custom_icon?.filename}
          iconBackground={iconBgClasses[color]}
          title={blok.title}
          subtitle={blok.text}
          className="flex-1"
        />
        {href && <ArrowRightThin className="size-5 shrink-0 text-black" />}
      </div>
    );

    return (
      <div {...storyblokEditable(blok)}>
        {href ? (
          <Link href={href} className="block hover:opacity-75 transition-opacity">
            {content}
          </Link>
        ) : (
          content
        )}
      </div>
    );
  }

  // Plain icon + text (like event hero list)
  const content = (
    <div className="flex items-center">
      <ListItem
        icon={blok.icon}
        customIconUrl={blok.custom_icon?.filename}
        className="flex-1"
      >
        {blok.text}
      </ListItem>
      {href && <ArrowRightThin className="size-5 shrink-0 text-black" />}
    </div>
  );

  return (
    <div {...storyblokEditable(blok)}>
      {href ? (
        <Link href={href} className="block hover:opacity-75 transition-opacity">
          {content}
        </Link>
      ) : (
        content
      )}
    </div>
  );
};

export default InfoIconListItem;
